import { useCallback, useEffect, useMemo, useState } from 'react';
import { navigate } from '@/hooks/useRoute';
import { useStore } from '@/context/StoreContext';
import { restGet } from '@/lib/supabase';
import './AdminDashboardV5.css';

type OrderRow={
  id:string;
  order_number:string;
  status:'payment_pending'|'paid'|'payment_failed'|'cancelled';
  total:number;
  customer:{firstName?:string;lastName?:string;phone?:string;email?:string}|null;
  items:Array<{qty?:number;name?:string}>|null;
  fulfillment_status:'received'|'processing'|'packing'|'shipped'|'delivered';
  admin_seen_at:string|null;
  created_at:string;
  paid_at:string|null;
};

type AnalyticsEvent={
  id:string;
  event_name:string;
  session_id:string|null;
  path:string|null;
  source:string|null;
  device:string|null;
  search_term:string|null;
  product_slug:string|null;
  metadata:Record<string,unknown>|null;
  created_at:string;
};

const money=(value:number)=>new Intl.NumberFormat('he-IL',{style:'currency',currency:'ILS',maximumFractionDigits:0}).format(Number(value||0));
const dayStart=()=>{const d=new Date();d.setHours(0,0,0,0);return d};
const daysAgo=(days:number)=>{const d=new Date();d.setDate(d.getDate()-days);return d};
const rel=(value:string)=>{const mins=Math.floor(Math.max(0,Date.now()-new Date(value).getTime())/60000);if(mins<1)return 'עכשיו';if(mins<60)return `לפני ${mins} דק׳`;const h=Math.floor(mins/60);if(h<24)return `לפני ${h} שע׳`;return `לפני ${Math.floor(h/24)} ימים`};
const statusText:Record<string,string>={paid:'שולם',payment_pending:'ממתין',payment_failed:'נכשל',cancelled:'בוטל'};
const eventText:Record<string,string>={page_view:'צפייה בעמוד',product_view:'צפייה במוצר',search:'חיפוש באתר',add_to_cart:'הוספה לסל',begin_checkout:'התחלת קופה',purchase:'רכישה',whatsapp_click:'לחיצה על WhatsApp'};

const routeLabel=(path:string|null,productSlug:string|null,products:Array<{slug:string;name:string}>)=>{
  if(productSlug){
    const product=products.find(item=>item.slug===productSlug);
    return product?.name||productSlug;
  }
  const raw=String(path||'');
  const [pathname,query='']=raw.split('?');
  const params=new URLSearchParams(query);
  if(pathname==='/'||pathname==='')return 'דף הבית';
  if(pathname==='/catalog'){
    const q=params.get('q');
    if(q)return `תוצאות חיפוש: ${q}`;
    if(params.get('sale')==='1')return 'SALE';
    const category=params.get('category')||'';
    const categories:Record<string,string>={new:'חדשים באתר',rings:'טבעות',earrings:'עגילים',necklaces:'שרשראות',bracelets:'צמידים'};
    return categories[category]||'כל התכשיטים';
  }
  const labels:Record<string,string>={
    '/cart':'סל קניות','/checkout':'קופה','/wishlist':'מועדפים','/account':'אזור אישי',
    '/ring-builder':'בונה הטבעת','/journal':'המגזין','/about':'אודות','/contact':'צור קשר',
    '/size-guide':'מדריך מידות','/stores':'מרכז הידע','/collections':'קולקציות',
    '/payment/success':'תשלום הושלם','/payment/failed':'תשלום נכשל',
  };
  if(labels[pathname])return labels[pathname];
  const productMatch=pathname.match(/^\/product\/(.+)$/);
  if(productMatch){
    const slug=decodeURIComponent(productMatch[1]);
    const product=products.find(item=>item.slug===slug);
    return product?.name||slug;
  }
  return raw||'—';
};

function SourceBars({items}:{items:Array<{name:string;count:number}>}){
  const max=Math.max(1,...items.map(item=>item.count));
  return <div className="mcc5-source-bars">{items.map(item=><div key={item.name}><span>{item.name}</span><i><b style={{width:`${Math.max(7,Math.round(item.count/max*100))}%`}}/></i><strong>{item.count}</strong></div>)}</div>;
}

export function AdminDashboard(){
  const {products,dataMode}=useStore();
  const [orders,setOrders]=useState<OrderRow[]>([]);
  const [events,setEvents]=useState<AnalyticsEvent[]>([]);
  const [loading,setLoading]=useState(true);
  const [analyticsReady,setAnalyticsReady]=useState(true);
  const [error,setError]=useState('');

  const load=useCallback(async(silent=false)=>{
    if(!silent)setLoading(true);
    setError('');
    try{
      const rows=await restGet<OrderRow[]>('mielley_orders','select=id,order_number,status,total,customer,items,fulfillment_status,admin_seen_at,created_at,paid_at&order=created_at.desc&limit=300');
      setOrders(rows);
    }catch(err){setError(err instanceof Error?err.message:'שגיאה בטעינת ההזמנות')}

    try{
      const since=encodeURIComponent(daysAgo(30).toISOString());
      const rows=await restGet<AnalyticsEvent[]>('mielley_analytics_events',`select=id,event_name,session_id,path,source,device,search_term,product_slug,metadata,created_at&created_at=gte.${since}&order=created_at.desc&limit=5000`);
      setEvents(rows);
      setAnalyticsReady(true);
    }catch{
      setEvents([]);
      setAnalyticsReady(false);
    }finally{if(!silent)setLoading(false)}
  },[]);

  useEffect(()=>{void load();const timer=window.setInterval(()=>{if(document.visibilityState==='visible')void load(true)},30000);return()=>window.clearInterval(timer)},[load]);

  const metrics=useMemo(()=>{
    const today=dayStart();
    const paid=orders.filter(o=>o.status==='paid');
    const todayPaid=paid.filter(o=>new Date(o.paid_at||o.created_at)>=today);
    const monthStart=new Date(new Date().getFullYear(),new Date().getMonth(),1);
    const monthPaid=paid.filter(o=>new Date(o.paid_at||o.created_at)>=monthStart);
    const visitors=new Set(events.filter(e=>e.event_name==='page_view').map(e=>e.session_id).filter(Boolean)).size;
    const todayVisitors=new Set(events.filter(e=>e.event_name==='page_view'&&new Date(e.created_at)>=today).map(e=>e.session_id).filter(Boolean)).size;
    const productViews=events.filter(e=>e.event_name==='product_view').length;
    const searches=events.filter(e=>e.event_name==='search').length;
    const carts=events.filter(e=>e.event_name==='add_to_cart').length;
    const checkout=events.filter(e=>e.event_name==='begin_checkout').length;
    const analyticsSessions=new Set(events.map(e=>e.session_id).filter(Boolean)).size;
    const purchaseSessions=new Set(events.filter(e=>e.event_name==='purchase').map(e=>e.session_id).filter(Boolean)).size;
    const conversion=analyticsSessions?purchaseSessions/analyticsSessions*100:0;
    const paid30=paid.filter(o=>new Date(o.paid_at||o.created_at)>=daysAgo(30));
    const avg=paid30.length?paid30.reduce((s,o)=>s+Number(o.total||0),0)/paid30.length:0;
    return {
      todayRevenue:todayPaid.reduce((s,o)=>s+Number(o.total||0),0),
      monthRevenue:monthPaid.reduce((s,o)=>s+Number(o.total||0),0),
      todayOrders:orders.filter(o=>new Date(o.created_at)>=today).length,
      newOrders:orders.filter(o=>o.status==='paid'&&!o.admin_seen_at).length,
      pending:orders.filter(o=>o.status==='payment_pending').length,
      active:paid.filter(o=>String(o.fulfillment_status||'received')!=='delivered').length,
      avg,visitors,todayVisitors,productViews,searches,carts,checkout,conversion,
    };
  },[orders,events]);

  const sources=useMemo(()=>{
    const map=new Map<string,number>();
    events.filter(e=>e.event_name==='page_view').forEach(e=>{const key=e.source||'Direct';if(key==='Internal')return;map.set(key,(map.get(key)||0)+1)});
    return [...map.entries()].map(([name,count])=>({name,count})).sort((a,b)=>b.count-a.count).slice(0,5);
  },[events]);

  const topSearches=useMemo(()=>{
    const map=new Map<string,number>();
    events.filter(e=>e.event_name==='search'&&e.search_term).forEach(e=>map.set(e.search_term!,1+(map.get(e.search_term!)||0)));
    return [...map.entries()].map(([term,count])=>({term,count})).sort((a,b)=>b.count-a.count).slice(0,5);
  },[events]);

  const topProducts=useMemo(()=>{
    const map=new Map<string,number>();
    events.filter(e=>e.event_name==='product_view'&&e.product_slug).forEach(e=>map.set(e.product_slug!,1+(map.get(e.product_slug!)||0)));
    return [...map.entries()].map(([slug,count])=>{
      const product=products.find(p=>p.slug===slug);
      return {slug,count,name:product?.name||slug,image:product?.image||''};
    }).sort((a,b)=>b.count-a.count).slice(0,4);
  },[events,products]);

  const live=events.slice(0,7);
  const recentOrders=orders.slice(0,5);

  return <div className="mcc5-dashboard" dir="rtl">
    <section className="mcc5-head">
      <div><span>MIELLEY CONTROL CENTER · V5</span><h2>מרכז הבקרה</h2><p>מכירות, הזמנות, תנועה והתנהגות לקוחות — במקום אחד.</p></div>
      <div className="mcc5-head-actions"><em className={analyticsReady?'on':'off'}>{analyticsReady?'● Analytics Live':'● Analytics Setup'}</em><button onClick={()=>void load()} disabled={loading}>↻ רענון</button></div>
    </section>

    {!analyticsReady&&<section className="mcc5-setup"><b>שלב אחד נשאר להפעלת מעקב הכניסות והחיפושים</b><span>יש להריץ את קובץ SQL שמגיע עם חבילת V5 ב־Supabase. המכירות וההזמנות ממשיכות לעבוד כרגיל.</span></section>}
    {error&&<section className="mcc5-error">{error}</section>}

    <section className="mcc5-kpis">
      <article><i>₪</i><span>הכנסות היום</span><b>{money(metrics.todayRevenue)}</b><small>{metrics.todayOrders} הזמנות היום</small></article>
      <article><i>▣</i><span>הזמנות חדשות</span><b>{metrics.newOrders}</b><small>{metrics.active} הזמנות בטיפול</small></article>
      <article><i>◎</i><span>מבקרים היום</span><b>{analyticsReady?metrics.todayVisitors:'—'}</b><small>{analyticsReady?`${metrics.visitors} ב־30 ימים`:'ממתין להפעלה'}</small></article>
      <article><i>◇</i><span>סל ממוצע</span><b>{money(metrics.avg)}</b><small>עסקאות ששולמו · 30 ימים</small></article>
      <article><i>↗</i><span>יחס המרה</span><b>{analyticsReady?`${metrics.conversion.toFixed(2)}%`:'—'}</b><small>{metrics.checkout} התחלות קופה</small></article>
    </section>

    <section className="mcc5-grid mcc5-grid-main">
      <article className="mcc5-card mcc5-sales">
        <header><div><span>BUSINESS</span><h3>מכירות והזמנות</h3></div><b>{money(metrics.monthRevenue)}</b></header>
        <div className="mcc5-sales-stat"><strong>{metrics.todayOrders}</strong><span>הזמנות היום</span></div>
        <div className="mcc5-sales-stat"><strong>{metrics.pending}</strong><span>ממתינות לתשלום</span></div>
        <div className="mcc5-sales-stat"><strong>{metrics.active}</strong><span>בטיפול ומשלוח</span></div>
        <button onClick={()=>navigate('/admin/orders')}>למרכז ההזמנות ←</button>
      </article>

      <article className="mcc5-card">
        <header><div><span>TRAFFIC</span><h3>מקורות כניסה</h3></div><button onClick={()=>navigate('/admin/analytics')}>דוח מלא</button></header>
        {analyticsReady&&sources.length?<SourceBars items={sources}/>:<div className="mcc5-empty">עדיין אין נתוני תנועה.</div>}
      </article>

      <article className="mcc5-card">
        <header><div><span>SEARCH</span><h3>חיפושים מובילים</h3></div><button onClick={()=>navigate('/admin/searches')}>כל החיפושים</button></header>
        <div className="mcc5-search-list">{topSearches.length?topSearches.map((item,index)=><div key={item.term}><em>{index+1}</em><span>{item.term}</span><b>{item.count}</b></div>):<div className="mcc5-empty">עדיין אין חיפושים להצגה.</div>}</div>
      </article>
    </section>

    <section className="mcc5-grid mcc5-grid-secondary">
      <article className="mcc5-card mcc5-products">
        <header><div><span>PRODUCTS</span><h3>המוצרים הכי נצפים</h3></div><small>{metrics.productViews} צפיות מוצר</small></header>
        <div className="mcc5-product-grid">{topProducts.length?topProducts.map(item=><button key={item.slug} onClick={()=>navigate(`/admin/products`)}>{item.image?<img src={item.image} alt=""/>:<i>◇</i>}<span>{item.name}</span><b>{item.count} צפיות</b></button>):<div className="mcc5-empty">הנתונים יופיעו לאחר תחילת המעקב.</div>}</div>
      </article>

      <article className="mcc5-card mcc5-live">
        <header><div><span>LIVE ACTIVITY</span><h3>פעילות אחרונה באתר</h3></div><small>{analyticsReady?'מתעדכן אוטומטית':'לא פעיל'}</small></header>
        <div className="mcc5-live-list">{live.length?live.map(event=><div key={event.id}><i className={`ev-${event.event_name}`}/><span><b>{eventText[event.event_name]||event.event_name}</b><small>{event.search_term?`חיפוש: ${event.search_term}`:routeLabel(event.path,event.product_slug,products)}</small></span><time>{rel(event.created_at)}</time></div>):<div className="mcc5-empty">אין עדיין אירועים להצגה.</div>}</div>
      </article>
    </section>

    <section className="mcc5-card mcc5-orders">
      <header><div><span>RECENT ORDERS</span><h3>הזמנות אחרונות</h3></div><button onClick={()=>navigate('/admin/orders')}>לכל ההזמנות</button></header>
      <div className="mcc5-order-list">{recentOrders.map(order=>{
        const count=order.items?.reduce((s,item)=>s+Number(item.qty||0),0)||0;
        return <button key={order.id} onClick={()=>navigate('/admin/orders')}><strong>{order.order_number}</strong><span>{order.customer?.firstName||'לקוחה'} {order.customer?.lastName||''}</span><span>{count} פריטים</span><b>{money(order.total)}</b><em className={order.status}>{statusText[order.status]}</em><small>{rel(order.created_at)}</small></button>;
      })}{!recentOrders.length&&<div className="mcc5-empty">עדיין אין הזמנות.</div>}</div>
    </section>

    <section className="mcc5-quick">
      <button onClick={()=>navigate('/admin/analytics')}><i>◫</i><span><b>אנליטיקה</b><small>כניסות, מקורות ומשפך המרה</small></span></button>
      <button onClick={()=>navigate('/admin/searches')}><i>⌕</i><span><b>חיפושים באתר</b><small>מה חיפשו ומה לא נמצא</small></span></button>
      <button onClick={()=>navigate('/admin/orders')}><i>₪</i><span><b>הזמנות</b><small>תשלום, טיפול ומשלוח</small></span></button>
      <button onClick={()=>navigate('/')}><i>◉</i><span><b>צפייה בחנות</b><small>{dataMode==='supabase'?'האתר החי':'תצוגה מקומית'}</small></span></button>
    </section>
  </div>;
}
